import sortBy from 'lodash/sortBy';
import { BRAND_STROKE_WIDTH } from '@mentimeter/global-variables';
import { contrastColor } from '@mentimeter/ragnar-colors';
import {
  scaleBand,
  scaleLinear,
  type ScaleBand,
  type ScaleLinear,
} from 'd3-scale';
import { curveCardinal, line as d3Line } from 'd3-shape';
import { max } from 'd3-array';
import { d3 } from './d3';
import { NORMAL_FONT_SIZE, LARGE_FONT_SIZE } from './utils';

const LAYOUT = {
  width: 960,
  height: 540,
};
const MARGIN = {
  top: 0,
  right: LAYOUT.width * 0.1,
  bottom: LAYOUT.height * 0,
  left: LAYOUT.width * 0.1,
};
const WIDTH = LAYOUT.width - MARGIN.right - MARGIN.left;
const HEIGHT = LAYOUT.height - MARGIN.top - MARGIN.bottom;
const Y_AXIS_PADDING = 10;
const TRANSITION_TIME = 1000;
const SCROLL_ANIMATION_TIME = 4000;
const MAX_RACE_TIME = 4000;
const MIN_RACE_TIME = 1500;
const MAX_INTERPOLATION_VALUE = 0.999;
const FIRST_DELAY = 300;
const SECOND_DELAY = FIRST_DELAY + TRANSITION_TIME;
const THIRD_DELAY = SECOND_DELAY + MAX_RACE_TIME;
const FOURTH_DELAY = THIRD_DELAY + TRANSITION_TIME * 1.75;
const FIFTH_DELAY = FOURTH_DELAY + TRANSITION_TIME * 1.5;
const SIXTH_DELAY = FIFTH_DELAY + TRANSITION_TIME;
const BAR_TEXT_PADDING = 8;
const BAR_MIN_WIDTH = 110;
const BAR_MAX_WIDTH = WIDTH - BAR_MIN_WIDTH;
const FADE_OPACITY = 0.2;
const BAR_CHANGE_OPACITY = 0.6;
const WINNER_SCALE = 2.5;
const WINNER_EMOJI_PADDING = 15;
const ROUND_SCORE_CIRCLE_RADIUS = 42;
const TIE_TEXT = "It's a tie!";

/* eslint-disable no-param-reassign */
function getInterpolationFormula(t: number, ease: string): number {
  // Formulas from source code available in d3 ease readme: https://github.com/d3/d3-ease/blob/master/README.md
  let interpolationValue = t;
  if (ease === 'exp') {
    interpolationValue =
      ((t *= 2) <= 1
        ? Math.pow(2, 10 * t - 10)
        : 2 - Math.pow(2, 10 - 10 * t)) / 2;
  } else if (ease === 'quad') {
    interpolationValue = ((t *= 2) <= 1 ? t * t : --t * (2 - t) + 1) / 2;
  } else if (ease === 'cubic') {
    interpolationValue = ((t *= 2) <= 1 ? t * t * t : (t -= 2) * t * t + 2) / 2;
  }
  // Guarantee the max value is reached when interpolating between a large number range, e.g., 0 --> 5000
  const shouldRoundUp = interpolationValue > MAX_INTERPOLATION_VALUE;
  return shouldRoundUp ? 1 : interpolationValue;
}

interface Question {
  score: number;
  correctAnswer: boolean;
  choiceId: number;
}
type Ease = 'exp' | 'quad' | 'cubic';
export interface Player {
  choiceId: number;
  correctAnswer: boolean;
  correct_answer: boolean;
  emojiName: string;
  emojiShortname: string;
  emojiURL: string;
  identifier: string;
  index: number;
  name: string;
  points: [number, number][];
  position: number;
  question: Question;
  questionScore: number;
  score: number;
  totalScore: number;
}
interface LeaderboardT {
  readonly selector: string;
  readonly players: Player[];
  readonly bg: string;
  readonly textColor: string;
  readonly playerColors: string[];
  readonly animationMultiplier: 0 | 1;
  readonly timeBasedScoring: boolean;
  readonly showQuizWinner: boolean;
  readonly removePlayerName:
    | undefined
    | ((payload: { identifier: string; name: string }) => void);
}

/**
 * Leaderboard: quiz scoreboard with "racing" animations, sorts top players
 */
export class Leaderboard implements LeaderboardT {
  readonly selector: string;
  readonly players: Player[];
  readonly bg: string;
  readonly textColor: string;
  readonly playerColors: string[];
  readonly animationMultiplier: 0 | 1;
  readonly timeBasedScoring: boolean;
  readonly showQuizWinner: boolean;
  readonly removePlayerName:
    | undefined
    | ((payload: { identifier: string; name: string }) => void);

  xMax = 1;
  x: ScaleLinear<number, number> = scaleLinear();
  y: ScaleBand<string> = scaleBand();
  data: Player[] = [];
  startData: Player[] = [];
  axisNode: d3.Selection<SVGGElement, string, d3.BaseType, unknown> | undefined;
  bars: d3.Selection<SVGGElement, Player, d3.BaseType, unknown> | undefined;
  barsEnter:
    | d3.Selection<SVGGElement, Player, d3.BaseType, unknown>
    | undefined;
  barScore: d3.Selection<SVGGElement, Player, d3.BaseType, unknown> | undefined;
  barScoreText:
    | d3.Selection<SVGTextElement, Player, d3.BaseType, unknown>
    | undefined;
  bar: d3.Selection<SVGGElement, Player, d3.BaseType, unknown> | undefined;
  roundScoresPath:
    | d3.Selection<SVGPathElement, [number, number][], d3.BaseType, unknown>
    | undefined;
  tieTextEnter:
    | d3.Selection<SVGGElement, string, d3.BaseType, unknown>
    | undefined;
  barChange:
    | d3.Selection<SVGGElement, Player, d3.BaseType, unknown>
    | undefined;
  fastest:
    | d3.Selection<SVGTextElement, Player, d3.BaseType, unknown>
    | undefined;
  names: d3.Selection<SVGGElement, Player, d3.BaseType, unknown> | undefined;
  emojis: d3.Selection<SVGGElement, Player, d3.BaseType, unknown> | undefined;
  showWinnerTimeout: NodeJS.Timeout | null = null;

  /**
   * @param selector: string, selection of a dom node, e.g., #leaderboard-${questionId}
   * @param players: array of players with name, score data, etc.
   * @param bg: string, hex color, e.g., #d8d8d8
   * @param textColor: string, hex color, e.g., #000000
   * @param playerColors: array of hex colors, e.g., ['blue', 'green', 'red']
   * @param animationMultiplier: value to multiply animation durations by, 1 (enabled) or 0 (disabled)
   * @param timeBasedScoring: boolean, decides whether the fastest answerer is relevant
   * @param showQuizWinner: boolean, decides whether the winner of the whole quiz should be revealed
   * @param removePlayerName: function, removes a custom avatar name and replaces it with the default
   */
  constructor(props: LeaderboardT) {
    this.selector = props.selector;
    this.players = props.players;
    this.bg = props.bg;
    this.textColor = props.textColor;
    this.playerColors = props.playerColors;
    this.animationMultiplier = props.animationMultiplier;
    this.timeBasedScoring = props.timeBasedScoring;
    this.showQuizWinner = props.showQuizWinner;
    this.removePlayerName = props.removePlayerName;
  }

  /**
   * Bump top scorer to end of array to ensure winning player has the highest z-index for the winner animation
   * @returns list of players with top scorer first
   */
  private bumpTopScorer = (): Player[] => {
    const totalScores = this.startData.map((d) => d.totalScore);
    const topScore = Math.max(...totalScores);
    const topScoreIndex = this.startData.findIndex(
      (d) => d.totalScore === topScore,
    );
    const topScorer = this.startData[topScoreIndex];
    if (topScorer) {
      this.startData.splice(topScoreIndex, 1);
      this.startData.push(topScorer);
    }
    return this.startData;
  };

  /**
   * Format and arrange data based on original players list
   *
   * @returns list of players
   */
  private getData = (): Player[] => {
    // Alternate scale to preserve master scale's domain
    const x = scaleLinear()
      .range([0, this.xMax])
      .domain([0, max(this.startData.map((d) => d.score)) ?? 0]);
    this.startData.forEach((d) => {
      let xPos =
        BAR_MIN_WIDTH -
        ROUND_SCORE_CIRCLE_RADIUS -
        BAR_TEXT_PADDING -
        this.y.bandwidth() / 2;
      if (d.score) {
        xPos =
          (max([BAR_MIN_WIDTH * 2, x(d.score)]) ?? 0) -
          BAR_TEXT_PADDING -
          ROUND_SCORE_CIRCLE_RADIUS -
          this.y.bandwidth() / 2;
      }
      d.points = [
        [xPos, this.y.bandwidth() / 2],
        [(xPos - ROUND_SCORE_CIRCLE_RADIUS) / 2, -this.y.bandwidth() / 2],
        [x(0) - ROUND_SCORE_CIRCLE_RADIUS, this.y.bandwidth() / 2],
      ];
    });
    const sortedData = this.bumpTopScorer();
    return sortedData;
  };

  /**
   * Generate a map of players for quick selection
   *
   * @returns player map
   */
  private getDataMap = (): { [id: string]: Player } => {
    const maps: Record<string, Player> = {};
    this.data.forEach((res) => {
      maps[res.identifier] = res;
    });
    return maps;
  };

  /**
   * Append a temporary text node to measure its computed size, then remove it
   *
   * @returns number, computed text width of the longest name (in pixels)
   */
  private getMaxTextWidth = (): number => {
    const textWidths: number[] = [];
    d3.select(this.selector)
      .selectAll<SVGGElement, Player>('.bar-group')
      .data(this.startData, (d: Player) => d.identifier)
      .enter()
      .insert('g', ':first-child')
      .attr('class', 'bar-group')
      .append('text')
      .attr('class', 'theme-text-color--fill')
      .attr('dy', '0.32em')
      .attr('font-size', NORMAL_FONT_SIZE)
      .style('cursor', 'default')
      .text((d) => d.name || d.emojiName)
      .each(function () {
        const thisWidth = this.getComputedTextLength();
        textWidths.push(thisWidth);
      });
    d3.select(this.selector).selectAll('.bar-group').remove();
    return Math.max(...textWidths);
  };

  /**
   * Generates a numeric range and padding for a d3 scaleBand
   *
   * @returns object, r: range interval and p: padding
   */
  private rangeBandOptions = (): { r: [number, number]; p: number } => {
    let reduceRange = 0;
    let ordinalPadding = 0.15;
    if (this.players.length === 1) {
      reduceRange = 0.1;
      ordinalPadding = 0.5;
    } else if (this.players.length === 2) {
      reduceRange = 0.2;
      ordinalPadding = 0.35;
    } else if (this.players.length <= 5) {
      reduceRange = 0.1;
      ordinalPadding = 0.25;
    }
    if (this.players.length >= 6) {
      ordinalPadding = 0.1;
    }
    const rangeInterval: [number, number] = [
      HEIGHT * reduceRange,
      HEIGHT - HEIGHT * reduceRange,
    ];
    return { r: rangeInterval, p: ordinalPadding };
  };

  /**
   * Generates a random animation time for each player in the race
   *
   * @returns array of numbers, time in ms
   */
  private getRandomAnimationDuration = (): number[] => {
    return this.data.map(() => {
      const randomDuration =
        this.animationMultiplier *
        (Math.floor(Math.random() * (MAX_RACE_TIME - MIN_RACE_TIME + 1)) +
          MIN_RACE_TIME);
      return randomDuration;
    });
  };

  /**
   * Generates a random easement function for each player in the race
   *
   * @returns array of easing function keys
   */
  private getRandomEaseFunction = (): Ease[] => {
    const easeBank = ['exp', 'quad', 'cubic'] as const;
    return this.data.map(() => {
      const randomEaseIndex = Math.floor(Math.random() * easeBank.length);
      return easeBank[randomEaseIndex] ?? easeBank[0];
    });
  };

  /**
   * Binds data to and appends group elements for each player
   */
  private updateBarGroup = (): void => {
    // Select
    this.bars = d3
      .select(this.selector)
      .selectAll<SVGGElement, Player>('.bars-group')
      .data(this.data);
    // Enter
    this.barsEnter = this.bars
      .enter()
      .append('g')
      .attr('class', 'bars-group')
      .attr('transform', (d) => `translate(0,${this.y(d.identifier)})`);
    // Exit
    this.bars.exit().remove();
  };

  /**
   * Creates a vertical axis for player scores
   */
  private updateAxis = (): void => {
    const maps = this.getDataMap();
    const axis = d3
      .axisLeft(this.y)
      .scale(this.y)
      .tickSize(0)
      .tickFormat(() => `${0}%`)
      .tickPadding(Y_AXIS_PADDING);
    this.axisNode = d3
      .select(this.selector)
      .selectAll<SVGGElement, string>('.y-axis')
      .data(['']) // Only append one
      .join('g')
      .attr('class', 'y-axis')
      .call(axis as any);
    this.axisNode
      .selectAll<SVGTextElement, string>('text')
      .text((d) => {
        let score = maps[d]?.totalScore;
        if (maps[d]?.score) {
          score = maps[d].totalScore - maps[d].score;
        }
        return `${score ?? 0} p`;
      })
      .attr('font-size', LARGE_FONT_SIZE)
      .style('font-weight', 600)
      .attr('color', this.textColor);
    this.axisNode
      .selectAll<SVGGElement, string>('.tick')
      .attr(
        'transform',
        (d) => `translate(0, ${(this.y(d) ?? 0) + this.y.bandwidth() / 2})`,
      );
    this.axisNode.selectAll('path').remove();
  };

  /**
   * Appends score elements for score change animation
   */
  private updateBarScore = (): void => {
    if (!this.barsEnter) {
      return;
    }
    // Enter
    this.barScore = this.barsEnter
      .append('g')
      .attr('class', 'score-group')
      .attr(
        'transform',
        `translate(${
          BAR_MIN_WIDTH - BAR_TEXT_PADDING - this.y.bandwidth() / 2
        }, ${this.y.bandwidth() / 2})`,
      )
      .style('opacity', 0);
    this.barScore
      .append('circle')
      .attr('class', 'score-circle')
      .attr('r', ROUND_SCORE_CIRCLE_RADIUS)
      .style('opacity', 0)
      .attr(
        'fill',
        (d) => this.playerColors[d.index % this.playerColors.length] ?? 'black',
      );
    this.barScoreText = this.barScore
      .append('text')
      .text('+0 p')
      .attr('class', 'score-text')
      .attr('fill', (d) => {
        const playerColor =
          this.playerColors[d.index % this.playerColors.length];
        return contrastColor(playerColor ?? 'black');
      })
      .attr('dy', '0.35em')
      .attr('text-anchor', 'middle')
      .attr('font-size', LARGE_FONT_SIZE);
  };

  /**
   * Appends elements to the main bar group for race animations
   */
  private updateBar = () => {
    if (!this.barsEnter) {
      return;
    }

    // Enter
    this.bar = this.barsEnter.append('g');
    this.bar
      .append('rect')
      .attr('class', 'bar-item')
      .attr('width', (d) => {
        let width = max([BAR_MIN_WIDTH, this.x(d.totalScore - d.score)]);
        if (d.score && this.x(d.totalScore - d.score) > BAR_MAX_WIDTH) {
          width = BAR_MAX_WIDTH;
        }
        return width ?? 0;
      })
      .attr('y', 0)
      .attr('height', this.y.bandwidth())
      .attr(
        'fill',
        (d) => this.playerColors[d.index % this.playerColors.length] ?? 'black',
      );
  };

  /**
   * Creates an invisible arc that the round score can follow when adding new points
   *
   * Debug or edit by setting a stroke and stroke-width to make the path visible
   */
  private updateRoundScoresPath = () => {
    if (!this.barsEnter) {
      return;
    }

    const points = this.data.map((d) => d.points);
    // Enter
    this.roundScoresPath = this.barsEnter
      .append('path')
      .data(points)
      .attr('class', 'path')
      .attr('fill', 'none')
      .attr('stroke', 'none')
      .attr('stroke-width', 0)
      .attr('d', d3Line().curve(curveCardinal));
  };

  /**
   * Binds data to and appends text in case of a tie on the winner screen
   */
  private updateTieText = () => {
    // Select
    const tieText = d3
      .select(this.selector)
      .selectAll<SVGGElement, string>('.tie-text')
      .data(['']);
    // Enter
    this.tieTextEnter = tieText
      .enter()
      .append('g')
      .attr('class', 'tie-text')
      .attr('transform', `translate(${WIDTH / 2}, ${HEIGHT / 2})`)
      .attr('opacity', 0);
    this.tieTextEnter
      .append('text')
      .text(TIE_TEXT)
      .attr('font-size', LARGE_FONT_SIZE * WINNER_SCALE)
      .attr('text-anchor', 'middle')
      .attr('transform', `translate(0, ${-LARGE_FONT_SIZE})`)
      .attr('fill', this.textColor);
    this.tieTextEnter
      .append('text')
      .attr('class', 'tie-text-names')
      .attr('font-size', LARGE_FONT_SIZE * WINNER_SCALE)
      .attr('text-anchor', 'middle')
      .attr('transform', `translate(0, ${LARGE_FONT_SIZE * 2})`)
      .attr('fill', this.textColor);
    // Exit
    tieText.exit().remove();
  };

  /**
   * Appends elements to show score changes per round
   */
  private updateBarChange = () => {
    if (!this.barsEnter) {
      return;
    }
    // Enter
    this.barChange = this.barsEnter.append('g');
    this.barChange
      .append('rect')
      .attr('class', 'bar-change')
      .attr('width', BAR_MIN_WIDTH)
      .attr('y', 0)
      .attr(
        'fill',
        (d) => this.playerColors[d.index % this.playerColors.length] ?? 'black',
      )
      .attr('fill-opacity', () => {
        if (this.animationMultiplier === 0) {
          return 1;
        }
        return BAR_CHANGE_OPACITY;
      })
      .attr('height', this.y.bandwidth());
  };

  /**
   * Appends text to show which player(s) are fastest this round
   */
  private updateFastest = () => {
    if (!this.names) {
      return;
    }
    const maxScore = max(this.data.map((d) => d.score));
    // Enter
    this.fastest = this.names
      .append('text')
      .attr('class', 'fastest')
      .attr('x', -this.y.bandwidth())
      .attr('y', `${LARGE_FONT_SIZE}px`)
      .style('opacity', 0)
      .style('font-style', 'italic')
      .text((d) =>
        d.score === maxScore && this.timeBasedScoring ? 'fastest' : '',
      )
      .attr('fill', this.textColor);
  };

  /**
   * Appends player names and enables name removal
   */
  private updateNames = () => {
    if (!this.barsEnter) {
      return;
    }
    const { removePlayerName } = this;
    // Enter
    this.names = this.barsEnter
      .append('g')
      .attr('class', 'name-group')
      .attr('transform', (d) => {
        let xPos =
          (max([BAR_MIN_WIDTH, this.x(d.totalScore - d.score)]) ?? 0) +
          this.y.bandwidth() / 2 +
          BAR_TEXT_PADDING;
        if (d.score && this.x(d.totalScore - d.score) > BAR_MAX_WIDTH) {
          xPos = BAR_MAX_WIDTH + this.y.bandwidth() / 2 + BAR_TEXT_PADDING;
        }
        return `translate(${xPos}, ${this.y.bandwidth() / 2})`;
      });
    const names = this.names
      .append('text')
      .attr('class', 'name-text')
      .attr('dy', '0.32em')
      .attr('font-size', LARGE_FONT_SIZE)
      .style('cursor', 'default')
      .text((d) => d.name)
      .attr('fill', this.textColor);

    if (removePlayerName) {
      // Add hover state if user can remove custom names
      names
        .on('mouseover', function () {
          const text = d3.select<SVGTextElement, Player>(this);
          text
            .filter((d) => text.text() !== d.emojiName)
            .attr('font-size', LARGE_FONT_SIZE * 1.1)
            .style('cursor', 'pointer')
            .style('fill', 'red')
            .attr('text-decoration', 'line-through');
        })
        .on('mouseout', function () {
          d3.select(this)
            .attr('font-size', LARGE_FONT_SIZE)
            .attr('text-decoration', 'none')
            .style('cursor', 'default')
            .style('fill', null);
        })
        .on('click', function () {
          const text = d3.select<SVGTextElement, Player>(this);
          text
            .filter((d) => text.text() !== d.emojiName)
            .each((d) =>
              removePlayerName({ identifier: d.identifier, name: d.emojiName }),
            )
            .text('')
            .attr('font-size', LARGE_FONT_SIZE)
            .attr('text-decoration', 'none')
            .style('cursor', 'default')
            .style('fill', null)
            .style('opacity', 0)
            .transition()
            .duration(TRANSITION_TIME)
            .style('opacity', 1)
            .text((d: Player) => d.emojiName);
        });
    }
  };

  /**
   * Appends player avatars
   */
  private updateEmojis = () => {
    if (!this.barsEnter) {
      return;
    }
    // Enter
    this.emojis = this.barsEnter
      .append('g')
      .attr('class', 'emoji')
      .attr('transform', (d) => {
        let xPos = max([BAR_MIN_WIDTH, this.x(d.totalScore)]);
        if (d.score && this.x(d.totalScore - d.score) > BAR_MAX_WIDTH) {
          xPos = BAR_MAX_WIDTH;
        } else if (d.score) {
          xPos = max([BAR_MIN_WIDTH, this.x(d.totalScore - d.score)]);
        }
        return `translate(${xPos}, ${this.y.bandwidth() / 2})`;
      });
    this.emojis
      .append('circle')
      .attr(
        'fill',
        (d) => this.playerColors[d.index % this.playerColors.length] ?? 'black',
      )
      .attr('r', this.y.bandwidth() / 2)
      .attr('stroke', this.bg)
      .attr('stroke-width', BRAND_STROKE_WIDTH);
    this.emojis
      .append('svg')
      .attr('width', this.y.bandwidth())
      .attr('height', this.y.bandwidth())
      .attr('y', -this.y.bandwidth() / 2)
      .attr('x', -this.y.bandwidth() / 2)
      .append('use')
      .attr('xlink:href', (d) => d.emojiURL)
      .attr('width', '100%')
      .attr('height', '100%');
  };

  /**
   * Shrink bars to prepare for race
   *
   * @returns array of resolved animation promises
   */
  private animation1 = (): Promise<void[]> => {
    if (!this.bar || !this.names || !this.emojis) {
      return Promise.resolve([]);
    }
    // Update
    const barTransition = this.bar
      .selectAll('rect')
      .transition('animation1-bar')
      .delay(this.animationMultiplier * FIRST_DELAY)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('width', BAR_MIN_WIDTH)
      .end();
    const namesTransition = this.names
      .transition('animation1-names')
      .delay(this.animationMultiplier * FIRST_DELAY)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr(
        'transform',
        `translate(
          ${BAR_MIN_WIDTH + this.y.bandwidth() / 2 + BAR_TEXT_PADDING},
          ${this.y.bandwidth() / 2}
        )`,
      )
      .end();
    const emojisTransition = this.emojis
      .transition('animation1-emojis')
      .delay(this.animationMultiplier * FIRST_DELAY)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr(
        'transform',
        `translate(${BAR_MIN_WIDTH}, ${this.y.bandwidth() / 2})`,
      )
      .end();

    return Promise.all([barTransition, namesTransition, emojisTransition]);
  };

  /**
   * Bar and score racing animation with random easements
   *
   * @returns array of resolved animation promises
   */
  private animation2 = (): Promise<void[]> => {
    if (
      !this.barChange ||
      !this.bar ||
      !this.barScore ||
      !this.barScoreText ||
      !this.names ||
      !this.emojis
    ) {
      return Promise.resolve([]);
    }

    const randomDurations = this.getRandomAnimationDuration();
    const randomEase = this.getRandomEaseFunction();
    const updateDomain = () =>
      this.x.domain([0, max(this.data.map((d) => d.score)) || 1]);

    // Update
    const barChangeTransition = this.barChange
      .transition('animation2-bar-change')
      .delay(this.animationMultiplier * SECOND_DELAY)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .ease(d3.easeLinear)
      .style('opacity', (d) => (d.score ? 1 : 0))
      .end();
    const barChangeRectTransition = this.barChange
      .selectAll<SVGRectElement, Player>('rect')
      .transition('animation2-bar-change-rect')
      .delay(this.animationMultiplier * SECOND_DELAY)
      .duration((d) => randomDurations[d.position - 1] ?? 0)
      .ease(d3.easeLinear)
      .attrTween('width', (d) => {
        updateDomain();
        let xMax = BAR_MIN_WIDTH;
        if (d.score) {
          xMax = max([BAR_MIN_WIDTH * 2, this.x(d.score)]) ?? 0;
        }
        const interpolate = d3.interpolateNumber(BAR_MIN_WIDTH, xMax);
        return (t) => factory(t, d.position, interpolate);
      })
      .end();
    const barScoreGroupTransition = this.bar
      .select<SVGGElement>('.score-group')
      .merge(this.barScore)
      .transition('animation2-score-group')
      .delay(this.animationMultiplier * SECOND_DELAY)
      .duration((d) => randomDurations[d.position - 1] ?? 0)
      .ease(d3.easeLinear)
      .attrTween('transform', (d) => {
        updateDomain();
        const xMin =
          BAR_MIN_WIDTH -
          ROUND_SCORE_CIRCLE_RADIUS -
          BAR_TEXT_PADDING -
          this.y.bandwidth() / 2;
        let xMax =
          BAR_MIN_WIDTH -
          ROUND_SCORE_CIRCLE_RADIUS -
          BAR_TEXT_PADDING -
          this.y.bandwidth() / 2;
        if (d.score) {
          xMax =
            (max([BAR_MIN_WIDTH * 2, this.x(d.score)]) ?? 0) -
            ROUND_SCORE_CIRCLE_RADIUS -
            BAR_TEXT_PADDING -
            this.y.bandwidth() / 2;
        }
        const interpolate = d3.interpolateNumber(xMin, xMax);
        return (t) =>
          `translate(${factory(t, d.position, interpolate)}, ${
            this.y.bandwidth() / 2
          })`;
      })
      .style('opacity', (d) =>
        d.score && this.animationMultiplier === 1 ? 1 : 0,
      )
      .end();
    const barScoreTextTransition = this.bar
      .select<SVGTextElement>('.score-text')
      .merge(this.barScoreText)
      .transition('animation2-score-text')
      .delay(this.animationMultiplier * SECOND_DELAY)
      .duration((d) => randomDurations[d.position - 1] ?? 0)
      .ease(d3.easeLinear)
      .tween('text', function (d) {
        const score = d.score || 0;
        const interpolate = d3.interpolateRound(0, score);
        // Use "this" so prettier doesn't correct to arrow function
        return (t) =>
          ((this as any).textContent = `+${factory(
            t,
            d.position,
            interpolate,
          )}`);
      })
      .end();
    const namesTransition = this.names
      .transition('animation2-names')
      .delay(this.animationMultiplier * SECOND_DELAY)
      .duration((d) => randomDurations[d.position - 1] ?? 0)
      .ease(d3.easeLinear)
      .attrTween('transform', (d) => {
        updateDomain();
        const xMin = BAR_MIN_WIDTH + this.y.bandwidth() / 2 + BAR_TEXT_PADDING;
        let xMax = xMin;
        if (d.score) {
          xMax =
            (max([BAR_MIN_WIDTH * 2, this.x(d.score)]) ?? 0) +
            this.y.bandwidth() / 2 +
            BAR_TEXT_PADDING;
        }
        const interpolate = d3.interpolateNumber(xMin, xMax);
        return (t) => `translate(
            ${factory(t, d.position, interpolate)},
            ${this.y.bandwidth() / 2}
          )`;
      })
      .end();
    const emojisTransition = this.emojis
      .transition('animation2-emojis')
      .delay(this.animationMultiplier * SECOND_DELAY)
      .duration((d) => randomDurations[d.position - 1] ?? 0)
      .ease(d3.easeLinear)
      .attrTween('transform', (d) => {
        updateDomain();
        let xMax = BAR_MIN_WIDTH;
        if (d.score) {
          xMax = max([BAR_MIN_WIDTH * 2, this.x(d.score)]) ?? 0;
        }
        const interpolate = d3.interpolateNumber(BAR_MIN_WIDTH, xMax);
        return (t) =>
          `translate(${factory(t, d.position, interpolate)}, ${
            this.y.bandwidth() / 2
          })`;
      })
      .end();

    // Factory function used for tween and attrTween
    function factory(
      t: number,
      position: number,
      interpolate: (t: number) => number,
    ) {
      const ease = randomEase[position - 1]!;
      const formula = getInterpolationFormula(t, ease);
      return `${interpolate(formula)}`;
    }

    return Promise.all([
      barChangeTransition,
      barChangeRectTransition,
      barScoreGroupTransition,
      barScoreTextTransition,
      namesTransition,
      emojisTransition,
    ]);
  };

  /**
   * Send round score to total score
   *
   * @returns array of resolved animation promises
   */
  private animation3 = (): Promise<void[]> => {
    if (!this.barScore || !this.roundScoresPath) {
      return Promise.resolve([]);
    }
    const { roundScoresPath } = this;
    const delay1 = THIRD_DELAY;
    const delay2 = delay1 + TRANSITION_TIME / 2;
    const delay3 = delay2 + TRANSITION_TIME;

    // Update
    const t1 = this.barScore
      .transition('animation3-round-score-transition1')
      .delay(this.animationMultiplier * delay1)
      .duration(this.animationMultiplier * TRANSITION_TIME * 2)
      .attrTween('transform', translateAlong())
      .end();
    const t2 = this.barScore
      .select('.score-circle')
      .transition('animation3-round-score-circle-transition')
      .delay(this.animationMultiplier * delay1)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .style('opacity', (d) => (d.score ? 1 : 0))
      .end();
    const t3 = this.barScore
      .transition('animation3-round-score-transition2')
      .delay(this.animationMultiplier * delay2)
      .duration((this.animationMultiplier * TRANSITION_TIME) / 2)
      .style('opacity', (d) => (d.score ? 1 : 0))
      .end();
    const t4 = this.barScore
      .transition('animation3-round-score-transition3')
      .delay(this.animationMultiplier * delay3)
      .duration((this.animationMultiplier * TRANSITION_TIME) / 2)
      .style('opacity', 0)
      .end();
    // Returns an attrTween for translating along the specified path element.
    // Source (point on path animation): https://bl.ocks.org/mbostock/1705868
    function translateAlong() {
      return (_: any, i: number) => {
        const path = roundScoresPath.filter((_, j) => j === i).node();
        const l = path?.getTotalLength() ?? 0;
        return (t: number) => {
          const p = path?.getPointAtLength(t * l) ?? { x: 0, y: 0 };
          return `translate(${p.x},${p.y})`;
        };
      };
    }

    return Promise.all([t1, t2, t3, t4]);
  };

  /**
   * Adjust bars for overall standing
   *
   * @returns array of resolved animation promises
   */
  private animation4 = (): Promise<void[]> => {
    if (
      !this.axisNode ||
      !this.bar ||
      !this.names ||
      !this.barChange ||
      !this.emojis ||
      !this.fastest
    ) {
      return Promise.resolve([]);
    }

    this.x.domain([0, max(this.data.map((d) => d.totalScore)) || 1]);
    const getFinalBarWidth = (d: Player) =>
      max([BAR_MIN_WIDTH, this.x(d.totalScore)]) ?? 0;
    const maps = this.getDataMap();
    const delay = FOURTH_DELAY;

    // Update
    const axisTextTransition = this.axisNode
      .selectAll<SVGTextElement, string>('text')
      .transition('animation4-axis-text')
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .tween('text', function (d: string) {
        let score = 0;
        let totalScore = 0;
        if (maps[d]) {
          score = maps[d].score;
          totalScore = maps[d].totalScore;
        }
        const currentScore = totalScore - score;
        const interpolate = d3.interpolateRound(currentScore, totalScore);
        // Use "this" so prettier doesn't correct to arrow function
        return (t) => ((this as any).textContent = `${interpolate(t)} p`);
      })
      .end();
    const barTransition = this.bar
      .selectAll<SVGRectElement, Player>('rect')
      .transition('animation4-bar')
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('width', getFinalBarWidth)
      .end();
    const barChangeTransition = this.barChange
      .selectAll<SVGRectElement, Player>('rect')
      .transition('animation4-bar-change')
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('width', getFinalBarWidth)
      .end();
    const barScoreTransition = this.bar
      .selectAll<SVGGElement, Player>('.score-group')
      .transition('animation4-bar-score')
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('transform', (d) => {
        const xPos =
          (max([BAR_MIN_WIDTH, this.x(d.totalScore)]) ?? 0) -
          ROUND_SCORE_CIRCLE_RADIUS -
          this.y.bandwidth() / 2;
        return `translate(${xPos}, ${this.y.bandwidth() / 2})`;
      })
      .end();
    const namesTransition = this.names
      .transition('animation4-names')
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('transform', (d) => {
        const xPos =
          (max([BAR_MIN_WIDTH, this.x(d.totalScore)]) ?? 0) +
          BAR_TEXT_PADDING +
          this.y.bandwidth() / 2;
        return `translate(${xPos}, ${this.y.bandwidth() / 2})`;
      })
      .end();
    const fastestTransition = this.fastest
      .transition('animation4-fastest')
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('x', 0)
      .style('opacity', BAR_CHANGE_OPACITY)
      .end();
    const emojisTransition = this.emojis
      .transition('animation4-emojis')
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('transform', (d) => {
        const xPos = max([BAR_MIN_WIDTH, this.x(d.totalScore)]);
        return `translate(${xPos}, ${this.y.bandwidth() / 2})`;
      })
      .end();

    return Promise.all([
      axisTextTransition,
      barTransition,
      barChangeTransition,
      barScoreTransition,
      namesTransition,
      fastestTransition,
      emojisTransition,
    ]);
  };

  /**
   * Sort bars by score (descending order)
   *
   * @returns array of resolved animation promises
   */
  private animation5 = (): Promise<void[]> => {
    if (!this.axisNode || !this.bars || !this.barsEnter) {
      return Promise.resolve([]);
    }

    const dataSorted = sortBy(this.players, (a) => a.position);
    this.y.domain(dataSorted.map((d) => d.identifier));
    const delay = FIFTH_DELAY;

    // Update
    const axisTransition = this.axisNode
      .selectAll<SVGGElement, string>('.tick')
      .transition('animation5-axis')
      .ease(d3.easeElastic)
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('transform', (d) => {
        return `translate(0, ${(this.y(d) ?? 0) + this.y.bandwidth() / 2})`;
      })
      .end();
    const barsTransition = this.bars
      .merge(this.barsEnter)
      .transition('animation5-bars')
      .ease(d3.easeElastic)
      .delay(this.animationMultiplier * delay)
      .duration(this.animationMultiplier * TRANSITION_TIME)
      .attr('transform', (d) => `translate(0,${this.y(d.identifier)})`)
      .end();

    return Promise.all([axisTransition, barsTransition]);
  };

  /**
   * Reveal the winner and fade out rest of leaderboard
   *
   * @param hasPositiveScore: boolean, run animation sooner if nobody scored this round
   */
  private animation6 = (hasPositiveScore: boolean): void => {
    if (
      !this.barChange ||
      !this.axisNode ||
      !this.bar ||
      !this.names ||
      !this.emojis ||
      !this.barScoreText
    ) {
      return;
    }

    const { y } = this;
    const barChange = this.barChange.selectAll<SVGRectElement, Player>(
      '.bar-change',
    );
    const scores = this.axisNode.selectAll('.tick');
    const scoreText = scores.selectAll<SVGTextElement, Player>('text');
    const topScore = max(this.data, (d) => d.totalScore);
    const delay = hasPositiveScore ? SIXTH_DELAY : SECOND_DELAY;
    const fadeOut = (
      selectors: d3.Selection<any, Player, d3.BaseType, any>[],
    ) => {
      selectors.forEach((selector) => {
        selector
          .transition()
          .delay(delay)
          .duration(TRANSITION_TIME)
          .style('opacity', (d) => {
            // Hide main bar completely when barChange is behind it
            const isMainBar = d.score && selector === this.bar;
            return isMainBar ? 0 : FADE_OPACITY;
          });
      });
    };
    fadeOut([
      this.bar,
      barChange,
      this.barScoreText,
      this.names,
      this.emojis,
      scoreText,
    ]);

    const winnerPlayers = this.data.filter((d) => d.totalScore === topScore);
    const winners = winnerPlayers.map((w) => w.name);
    const multipleWinners = winners.length > 1;
    const winnerId = this.data.find((d) => d.totalScore === topScore);
    const winnerIdentifier = winnerId ? winnerId.identifier : '';

    const getWinner = (
      selector: d3.Selection<SVGGElement, Player, d3.BaseType, unknown>,
    ) => selector.filter((d) => d.identifier === winnerIdentifier);
    const winnerEmoji = getWinner(this.emojis);
    const winnerName = getWinner(this.names);
    const winnerScore = scores.filter((d) => d === winnerIdentifier);
    // Use timeout instead of delay to ensure that node width calculations are
    // done at the right time, otherwise positions will be incorrect
    this.showWinnerTimeout = setTimeout(() => {
      if (multipleWinners && this.tieTextEnter) {
        const separator = winners.length > 2 ? ', ' : ' '; // No comma needed for list of 2
        const lastWinner = winners[winners.length - 1];
        winners[winners.length - 1] = `and ${lastWinner}`; // e.g., Player3 => and Player3
        const listOfWinners = winners.join(separator);
        const congratsText = `Awesome job ${listOfWinners}!`;
        const tieTextNames = this.tieTextEnter
          .select('.tie-text-names')
          .text(congratsText);
        const tieTextNode = tieTextNames.node();
        const textWidth = tieTextNode
          ? (tieTextNode as SVGTextElement).getBBox().width
          : 0;
        if (textWidth > window.innerWidth) {
          const numTiedPlayers = winners.length;
          const scroll = () => {
            if (!this.tieTextEnter) return;

            this.tieTextEnter
              .transition()
              .duration(TRANSITION_TIME)
              .attr('opacity', 1);
            this.tieTextEnter
              .select('.tie-text-names')
              .attr('text-anchor', 'start')
              .attr(
                'transform',
                `translate(${window.innerWidth}, ${LARGE_FONT_SIZE * 2})`,
              )
              .transition()
              .duration(SCROLL_ANIMATION_TIME * numTiedPlayers)
              .ease(d3.easeLinear)
              .attr('transform', function () {
                const textWidth = (this as SVGTextElement).getBBox().width;
                return `translate(${-window.innerWidth - textWidth}, ${
                  LARGE_FONT_SIZE * 2
                })`;
              })
              .on('end', scroll);
          };
          scroll();
        } else {
          this.tieTextEnter
            .transition()
            .duration(TRANSITION_TIME)
            .attr('opacity', 1);
        }
      } else {
        winnerEmoji
          .transition()
          .duration(TRANSITION_TIME)
          .style('opacity', 1)
          .attr('transform', () => {
            const yPos = HEIGHT / 2 - (this.y(winnerIdentifier) ?? 0);
            return `translate(${WIDTH / 2}, ${yPos}) scale(${WINNER_SCALE})`;
          });
        winnerName
          .transition()
          .duration(TRANSITION_TIME)
          .style('opacity', 1)
          .attr('transform', function (this: SVGGraphicsElement) {
            const nodeWidth = this.getBBox().width;
            const radius = y.bandwidth() * WINNER_SCALE;
            const startPosition = hasPositiveScore ? 0 : BAR_MIN_WIDTH;
            const xPos =
              WIDTH / 2 - (nodeWidth / 2) * WINNER_SCALE + startPosition;
            const yPos =
              HEIGHT / 2 -
              radius / 2 -
              WINNER_EMOJI_PADDING * WINNER_SCALE -
              (y(winnerIdentifier) ?? 0);
            return `translate(${xPos}, ${yPos}) scale(${WINNER_SCALE})`;
          })
          .selectAll('.fastest')
          .style('opacity', 0);
        winnerScore
          .transition()
          .duration(TRANSITION_TIME)
          .attr('transform', function () {
            const nodeWidth = (this as SVGTextElement).getBBox().width;
            const radius = y.bandwidth() * WINNER_SCALE;
            const xPos =
              WIDTH / 2 + (nodeWidth / 2) * WINNER_SCALE + Y_AXIS_PADDING;
            const yPos =
              HEIGHT / 2 +
              radius / 2 +
              WINNER_EMOJI_PADDING * WINNER_SCALE +
              BAR_TEXT_PADDING;
            return `translate(${xPos}, ${yPos}) scale(${WINNER_SCALE})`;
          })
          .selectAll('text')
          .style('opacity', 1);
      }
    }, this.animationMultiplier * delay);
  };

  /**
   * Clears timeouts used to show the winner
   */
  public cleanup = (): void => {
    if (this.showWinnerTimeout) clearTimeout(this.showWinnerTimeout);
  };

  /**
   * Visualization updater
   *
   * @remarks
   *  Must resolve animations before other chained commands can be executed, like taking a screenshot with PhantomJS
   *
   * @returns array of methods that return promises (animations)
   */
  public update = (): Promise<void[][]> => {
    d3.select(this.selector).attr(
      'transform',
      `translate(${MARGIN.left}, ${MARGIN.top})`,
    );
    const rangeOptions = this.rangeBandOptions();
    this.startData = sortBy(this.players, (a) => -(a.totalScore - a.score));
    const maxNameWidth = this.getMaxTextWidth();
    this.xMax = WIDTH - maxNameWidth;
    this.x
      .range([0, this.xMax])
      .domain([0, max(this.startData.map((d) => d.totalScore - d.score)) || 1]);
    this.y
      .rangeRound(rangeOptions.r)
      .domain(this.startData.map((d) => d.identifier))
      .padding(rangeOptions.p);
    this.data = this.getData();

    this.updateAxis();
    this.updateBarGroup();
    this.updateBarChange();
    this.updateBar();
    this.updateBarScore();
    this.updateRoundScoresPath();
    this.updateNames();
    this.updateFastest();
    this.updateEmojis();
    this.updateTieText();
    const hasPositiveScore = this.players.some((player) => player.score > 0);
    const hasPositiveTotalScore = this.players.some(
      (player) => player.totalScore > 0,
    );
    const showWinner =
      this.players.length > 0 && hasPositiveTotalScore && this.showQuizWinner;
    const animations = [];
    if (hasPositiveScore) {
      animations.push(this.animation1());
      animations.push(this.animation2());
      if (this.animationMultiplier === 1) {
        animations.push(this.animation3());
      }
      animations.push(this.animation4());
      animations.push(this.animation5());
    }
    // Winner animations need not return a promise, as they are skipped in preview mode
    if (showWinner && this.animationMultiplier === 1) {
      this.animation6(hasPositiveScore);
    }

    return Promise.all(animations);
  };
}
